'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { UserCommand } from '@repo/features/user';
import { GetUserSchema } from '@repo/schema/user';
import { useQuery } from '@repo/tanstack/query';
import { Button, buttonVariants, Field, FieldContent, FieldDescription, FieldError, FieldLabel, Popover, PopoverContent, PopoverTrigger, Spinner } from '@repo/ui/components';
import { ChevronDown, Search } from '@repo/ui/icons';
import { VariantProps } from '@repo/ui/lib/utils';
import { cn } from '@repo/ui/lib/utils';
import { useStore } from '@tanstack/react-form';
import { ComponentProps, ReactNode, useState } from 'react';
import { useFieldContext } from '../../lib/form-context.ts';
const getUserTitle = (user: {
  last_name?: null | string;
  name: string;
}) => {
  return [user.name, user.last_name].filter(Boolean).join(' ');
};
const getSelectedUser = (user: unknown) => {
  const directUser = GetUserSchema.safeParse(user);
  if (directUser.success) {
    return directUser.data;
  }
  if (user && typeof user === 'object' && 'data' in user) {
    const wrappedUser = GetUserSchema.safeParse(user.data);
    if (wrappedUser.success) {
      return wrappedUser.data;
    }
  }
  return null;
};
export const UserInput = t0 => {
  const $ = _c(46);
  const {
    className,
    companyId,
    description,
    disabled,
    isPending,
    label,
    placeholder: t1,
    variant: t2
  } = t0;
  const placeholder = t1 === undefined ? "-" : t1;
  const variant = t2 === undefined ? "outline" : t2;
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const field = useFieldContext();
  const selectedUserId = useStore(field.store, _temp);
  const api = useApi();
  let t3;
  if ($[0] !== api.users.getSimple || $[1] !== selectedUserId) {
    t3 = api.users.getSimple.queryOptions({
      path: {
        id: selectedUserId ?? 0
      }
    }, {
      enabled: !!selectedUserId
    });
    $[0] = api.users.getSimple;
    $[1] = selectedUserId;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: user
  } = useQuery(t3);
  let displayTitle;
  if ($[3] !== placeholder || $[4] !== selectedUser || $[5] !== selectedUserId || $[6] !== user) {
    const selectedUserData = getSelectedUser(user);
    displayTitle = placeholder;
    if (selectedUserId && selectedUser && selectedUser.id === selectedUserId) {
      displayTitle = selectedUser.title;
    } else {
      if (selectedUserId && selectedUserData) {
        displayTitle = getUserTitle(selectedUserData);
      }
    }
    $[3] = placeholder;
    $[4] = selectedUser;
    $[5] = selectedUserId;
    $[6] = user;
    $[7] = displayTitle;
  } else {
    displayTitle = $[7];
  }
  let t4;
  if ($[8] !== field) {
    t4 = value => {
      field.handleChange(value.id);
      setSelectedUser(value);
      setOpen(false);
    };
    $[8] = field;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const handleOnChange = t4;
  let t5;
  if ($[10] !== label) {
    t5 = label && <FieldLabel>{label}</FieldLabel>;
    $[10] = label;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== className) {
    t6 = cn("h-8 justify-between gap-1.5 py-1 pr-1 pl-2.5", className);
    $[12] = className;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const t7 = disabled || isPending;
  const t8 = !selectedUserId && "text-muted-foreground";
  let t9;
  if ($[14] !== t8) {
    t9 = cn(t8, "truncate", "flex items-center gap-1.5");
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Search data-sentry-element="Search" data-sentry-source-file="user-input.tsx" />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== displayTitle || $[18] !== t9) {
    t11 = <span className={t9}>{t10}{displayTitle}</span>;
    $[17] = displayTitle;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ChevronDown data-sentry-element="ChevronDown" data-sentry-source-file="user-input.tsx" />;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== isPending) {
    t13 = isPending && <Spinner className="size-4 shrink-0" />;
    $[21] = isPending;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t11 || $[24] !== t13 || $[25] !== t6 || $[26] !== t7 || $[27] !== variant) {
    t14 = <PopoverTrigger asChild={true} data-sentry-element="PopoverTrigger" data-sentry-source-file="user-input.tsx"><Button className={t6} data-testid="form-company-input-trigger" disabled={t7} type="button" variant={variant} data-sentry-element="Button" data-sentry-source-file="user-input.tsx">{t11}{t12}{t13}</Button></PopoverTrigger>;
    $[23] = t11;
    $[24] = t13;
    $[25] = t6;
    $[26] = t7;
    $[27] = variant;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== companyId || $[30] !== disabled || $[31] !== handleOnChange) {
    t15 = <PopoverContent align="start" className="max-w-100 p-0" collisionPadding={10} side="bottom" data-sentry-element="PopoverContent" data-sentry-source-file="user-input.tsx"><UserCommand companyId={companyId} disabled={disabled} onSelect={handleOnChange} data-sentry-element="UserCommand" data-sentry-source-file="user-input.tsx" /></PopoverContent>;
    $[29] = companyId;
    $[30] = disabled;
    $[31] = handleOnChange;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== open || $[34] !== t14 || $[35] !== t15) {
    t16 = <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="user-input.tsx"><Popover onOpenChange={setOpen} open={open} data-sentry-element="Popover" data-sentry-source-file="user-input.tsx">{t14}{t15}</Popover></FieldContent>;
    $[33] = open;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== description) {
    t17 = description && <FieldDescription>{description}</FieldDescription>;
    $[37] = description;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== field.state.meta.errors) {
    t18 = <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="user-input.tsx" />;
    $[39] = field.state.meta.errors;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t16 || $[42] !== t17 || $[43] !== t18 || $[44] !== t5) {
    t19 = <Field className="gap-1" data-sentry-element="Field" data-sentry-component="UserInput" data-sentry-source-file="user-input.tsx">{t5}{t16}{t17}{t18}</Field>;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
    $[44] = t5;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
};
function _temp(store) {
  return store.value;
}